import type { AdminSelf, ApiResult } from '@saay/api-client';
import {
  formatCount,
  formatLocalDate,
  formatRatePercent,
  formatTimestamp,
  normalizeMobileField,
  statusLabel,
  type LocalDate,
} from '@saay/formatting';
import { KpiCard, Money, SectionHeader, Surface } from '@saay/blocks';
import { Tabs as ShadTabs, TabsList, TabsTrigger } from '@saay/shadcn/components/tabs';
import {
  BuildingIcon,
  MegaphoneIcon,
  StoreIcon,
  TriangleAlertIcon,
  UsersIcon,
  WalletIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, type UseFormReturn } from 'react-hook-form';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';

import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { CommandDialog } from '../components/command-dialog.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { normalizeAdminCode } from '../components/form.js';
import { ListFooter, ListLoadMore } from '../components/list-footer.js';
import { ListSearchInput } from '../components/list-filters.js';
import {
  Button,
  EmptyState,
  PageHeader,
  PercentField,
  Section,
  Skeleton,
  TextField,
  useSearchInput,
  useToast,
} from '../kit.js';
import { marketingLabel } from '../marketing-labels.js';
import type {
  MarketingLayerInput,
  MarketingRecipient,
  MarketingRecipientCreate,
  MarketingStructure,
  MarketingStructureCreate,
  MarketingStructureSummary,
  MarketingStructureVersion,
  MarketingStructureVersionUpdate,
} from '../marketing-types.js';
import { canMarket, canUse } from '../permissions.js';
import {
  firstPageTotal,
  listStatus,
  pageRows,
  setupKeys,
  useAdminMarketingSummary,
  useMarketingRecipient,
  useMarketingRecipients,
  useMarketingStructure,
  useMarketingStructures,
} from '../queries.js';
import { reasonSchema } from '../validation.js';
import {
  CodeText,
  KpiFigure,
  MarketingStatus,
  marketingGuidance,
  phoneSummary,
} from './marketing-ui.js';

export interface MarketingOutletContext {
  readonly self: AdminSelf;
}

const LINKS = [
  { end: true, label: 'خلاصه', to: '/marketing' },
  { end: false, label: 'بازاریاب‌ها', to: '/marketing/recipients' },
  { end: false, label: 'ساختارها', to: '/marketing/structures' },
  { end: false, label: 'اتصال‌ها', to: '/marketing/assignments' },
  { end: false, label: 'کمیسیون‌ها', to: '/marketing/commissions' },
  { end: false, label: 'پرداخت‌ها', to: '/marketing/payouts' },
  { end: false, label: 'توقف پرداخت', to: '/marketing/holds' },
] as const;

function activeSection(pathname: string): string {
  const match = LINKS.filter((link) =>
    link.end ? pathname === link.to : pathname === link.to || pathname.startsWith(`${link.to}/`),
  ).sort((left, right) => right.to.length - left.to.length)[0];
  return match?.to ?? '/marketing';
}

const CODE = z
  .string()
  .transform(normalizeAdminCode)
  .refine(
    (value) => /^[A-Z0-9][A-Z0-9-]{2,31}$/.test(value),
    'کد را با حرف یا عدد انگلیسی شروع کنید.',
  );

function showDate(value: string): string {
  return formatLocalDate(value as LocalDate);
}

function MarketingSectionTabs() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = activeSection(pathname);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      scroller.current
        ?.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-active]')
        ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [current]);
  return (
    <ShadTabs
      className="w-full min-w-0"
      onValueChange={(next) => {
        if (next !== current) void navigate(next);
      }}
      value={current}
    >
      <div className="w-full min-w-0" ref={scroller}>
        <TabsList aria-label="بخش‌های بازاریابی">
          {LINKS.map((link) => (
            <TabsTrigger
              className="shrink-0 px-3 data-active:bg-primary data-active:text-primary-foreground dark:data-active:bg-primary dark:data-active:text-primary-foreground"
              key={link.to}
              value={link.to}
            >
              {link.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </ShadTabs>
  );
}

export function MarketingLayout() {
  return (
    <RequireArea area="marketing">
      {(self) => (
        <div className="flex min-w-0 flex-col gap-[var(--saay-layout-section-gap,1.5rem)]">
          <MarketingSectionTabs />
          <Outlet context={{ self } satisfies MarketingOutletContext} />
        </div>
      )}
    </RequireArea>
  );
}

export function MarketingSummaryPage() {
  usePageTitle('مدیریت بازاریابی');
  const summary = useAdminMarketingSummary();
  const data = summary.data;
  return (
    <>
      <PageHeader title="مدیریت بازاریابی" />
      <SectionHeader
        hint="ساختار کمیسیون، بازاریاب‌ها و اتصال آن‌ها به فروشگاه یا سازمان."
        title="بازاریابی"
      />
      <QueryState
        error={summary.error}
        onRetry={() => void summary.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری بازاریابی…" lines={6} />}
        status={summary.status}
      >
        {data === undefined ? null : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <KpiCard
              foot={<Link to="/marketing/structures">ساختارها</Link>}
              icon={MegaphoneIcon}
              label="ساختار منتشرشده"
              tone="olive"
              value={<KpiFigure>{formatCount(data.publishedStructureVersionCount)}</KpiFigure>}
            />
            <KpiCard
              foot={<Link to="/marketing/recipients">بازاریاب‌ها</Link>}
              icon={UsersIcon}
              label="بازاریاب فعال"
              tone="forest"
              value={<KpiFigure>{formatCount(data.activeRecipientCount)}</KpiFigure>}
            />
            <KpiCard
              foot={`${formatCount(data.merchantsWithActiveAssignment.count)} از ${formatCount(data.merchantsWithActiveAssignment.activeMerchantCount)} فروشگاه فعال`}
              icon={StoreIcon}
              label="فروشگاه متصل"
              tone="gold"
              value={<KpiFigure>{formatCount(data.merchantsWithActiveAssignment.count)}</KpiFigure>}
            />
            <KpiCard
              foot={<Link to="/marketing/assignments">اتصال‌ها</Link>}
              icon={BuildingIcon}
              label="سازمان متصل"
              tone="clay"
              value={
                <KpiFigure>{formatCount(data.organizationsWithActiveAssignmentCount)}</KpiFigure>
              }
            />
            <KpiCard
              foot="پیش‌نویس ناقص"
              icon={TriangleAlertIcon}
              label="نیازمند تکمیل"
              tone="copper"
              value={<KpiFigure>{formatCount(data.incompleteAssignmentCount)}</KpiFigure>}
            />
            <KpiCard
              foot={
                data.nextPayout === null
                  ? 'پرداخت بعدی نیست'
                  : `سررسید ${showDate(data.nextPayout.scheduledPayoutDate)}`
              }
              icon={WalletIcon}
              label="خالص پرداخت بعدی"
              tone="olive"
              value={
                data.nextPayout === null ? (
                  <KpiFigure>—</KpiFigure>
                ) : (
                  <Money rials={data.nextPayout.payableIrr} size="lg" />
                )
              }
            />
          </div>
        )}
      </QueryState>
    </>
  );
}

function RecipientColumns(): readonly DataTableColumn<MarketingRecipient>[] {
  return [
    {
      header: 'بازاریاب',
      id: 'name',
      render: (row) => (
        <Link to={`/marketing/recipients/${row.id}`}>{row.user.displayNameFa ?? 'بدون نام'}</Link>
      ),
    },
    {
      header: 'کد',
      id: 'code',
      render: (row) => <CodeText>{row.recipientCode}</CodeText>,
    },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => <MarketingStatus value={row.status} />,
    },
    {
      header: 'حساب',
      id: 'account',
      render: (row) => (row.payoutAccountVerified ? 'تأییدشده' : 'تأیید نشده'),
    },
  ];
}

const recipientSchema = z
  .object({
    firstNameFa: z.string().trim().max(100),
    lastNameFa: z.string().trim().max(100),
    mobileE164: z.string().trim(),
    mode: z.enum(['existing', 'new']),
    recipientCode: z
      .string()
      .transform(normalizeAdminCode)
      .refine(
        (value) => value === '' || /^[A-Z0-9][A-Z0-9-]{2,31}$/.test(value),
        'کد را با حرف یا عدد انگلیسی شروع کنید.',
      ),
    userId: z.string().trim(),
  })
  .superRefine((value, context) => {
    if (value.mode === 'existing' && value.userId === '') {
      context.addIssue({ code: 'custom', message: 'شناسه شخص را وارد کنید.', path: ['userId'] });
    }
    if (value.mode === 'new') {
      if (value.firstNameFa === '') {
        context.addIssue({ code: 'custom', message: 'نام را بنویسید.', path: ['firstNameFa'] });
      }
      if (value.lastNameFa === '') {
        context.addIssue({
          code: 'custom',
          message: 'نام خانوادگی را بنویسید.',
          path: ['lastNameFa'],
        });
      }
      if (normalizeMobileField(value.mobileE164) === '') {
        context.addIssue({
          code: 'custom',
          message: 'شماره موبایل را بنویسید.',
          path: ['mobileE164'],
        });
      }
    }
  });

type RecipientValues = z.input<typeof recipientSchema>;

function RecipientFields({ form }: { readonly form: UseFormReturn<RecipientValues> }) {
  const mode = form.watch('mode');
  const errors = form.formState.errors;
  return (
    <>
      <TextField
        error={errors.recipientCode?.message}
        hint="خالی بگذارید تا خودکار ساخته شود."
        label="کد بازاریاب"
        ltr
        {...form.register('recipientCode', {
          onChange: (event) => {
            event.target.value = normalizeAdminCode(event.target.value);
          },
        })}
      />
      <Controller
        control={form.control}
        name="mode"
        render={({ field }) => (
          <div className="flex gap-2" role="group" aria-label="شخص">
            <Button
              onClick={() => field.onChange('new')}
              type="button"
              variant={mode === 'new' ? 'primary' : 'secondary'}
            >
              شخص تازه
            </Button>
            <Button
              onClick={() => field.onChange('existing')}
              type="button"
              variant={mode === 'existing' ? 'primary' : 'secondary'}
            >
              شخص موجود
            </Button>
          </div>
        )}
      />
      {mode === 'existing' ? (
        <TextField
          error={errors.userId?.message}
          ltr
          label="شناسه شخص"
          {...form.register('userId')}
        />
      ) : (
        <>
          <TextField
            error={errors.firstNameFa?.message}
            label="نام"
            {...form.register('firstNameFa')}
          />
          <TextField
            error={errors.lastNameFa?.message}
            label="نام خانوادگی"
            {...form.register('lastNameFa')}
          />
          <TextField
            error={errors.mobileE164?.message}
            ltr
            label="شماره موبایل"
            {...form.register('mobileE164')}
          />
        </>
      )}
    </>
  );
}

function CreateRecipientDialog({
  onOpenChange,
}: {
  readonly onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  return (
    <CommandDialog<RecipientValues, MarketingRecipientCreate, MarketingRecipient>
      defaultValues={{
        firstNameFa: '',
        lastNameFa: '',
        mobileE164: '',
        mode: 'new',
        recipientCode: '',
        userId: '',
      }}
      fields={['recipientCode', 'userId', 'firstNameFa', 'lastNameFa', 'mobileE164']}
      guidance={marketingGuidance}
      name="marketing.recipient.create"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.marketingRecipients });
        toast.success({ confirmedBy: result, title: 'بازاریاب ثبت شد' });
        void navigate(`/marketing/recipients/${result.data.id}`);
      }}
      schema={recipientSchema as never}
      send={(client, body, intent) => client.post('/admin/marketing-recipients', { body, intent })}
      submitLabel="ثبت بازاریاب"
      title="بازاریاب تازه"
      toBody={(values) => {
        const parsed = recipientSchema.parse(values);
        if (parsed.mode === 'existing') {
          return {
            ...(parsed.recipientCode === '' ? {} : { recipientCode: parsed.recipientCode }),
            userId: parsed.userId,
          };
        }
        return {
          newUser: {
            firstNameFa: parsed.firstNameFa,
            lastNameFa: parsed.lastNameFa,
            mobileE164: normalizeMobileField(parsed.mobileE164),
          },
          userId: null,
          ...(parsed.recipientCode === '' ? {} : { recipientCode: parsed.recipientCode }),
        };
      }}
    >
      {(form) => <RecipientFields form={form} />}
    </CommandDialog>
  );
}

export function MarketingRecipientsPage() {
  usePageTitle('بازاریاب‌ها');
  const { self } = useOutletContext<MarketingOutletContext>();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const list = useMarketingRecipients({
    ...(q === '' ? {} : { q }),
    ...(status === '' ? {} : { status }),
  });
  const rows = pageRows(list.data);
  const [creating, setCreating] = useState(false);
  const manage = canMarket(self, 'createMarketingRecipient');
  const search = useSearchInput({
    applied: q,
    minLength: 1,
    onApply: (value) =>
      setParams((current) => {
        const next = new URLSearchParams(current);
        if (value === '') next.delete('q');
        else next.set('q', value);
        return next;
      }),
  });
  return (
    <>
      <PageHeader
        actions={
          manage ? (
            <Button onClick={() => setCreating(true)} type="button">
              بازاریاب تازه
            </Button>
          ) : undefined
        }
        title="بازاریاب‌ها"
      />
      <QueryState
        empty={<EmptyState title="بازاریابی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری بازاریاب‌ها…" lines={6} />}
        status={listStatus(list)}
      >
        <Section id="marketing-recipients" title="فهرست بازاریاب‌ها">
          <FilterPillBar
            activeFilterCount={[q, status].filter(Boolean).length}
            label="فیلتر بازاریاب‌ها"
            onClearAll={() => setParams({})}
          >
            <form
              className="shrink-0"
              onSubmit={(event) => {
                event.preventDefault();
                search.submit();
              }}
            >
              <ListSearchInput
                label="جست‌وجوی بازاریاب"
                onChange={(event) => search.setText(event.target.value)}
                placeholder="نام یا کد بازاریاب"
                value={search.text}
              />
            </form>
            <FilterPill
              emptyLabel="همه وضعیت‌ها"
              label="وضعیت"
              onChange={(value) =>
                setParams((current) => {
                  const next = new URLSearchParams(current);
                  if (value === '') next.delete('status');
                  else next.set('status', value);
                  return next;
                })
              }
              options={(['ACTIVE', 'INACTIVE'] as const).map((value) => ({
                label: statusLabel('activeStatus', value),
                value,
              }))}
              value={status}
            />
          </FilterPillBar>
          <AdminListTable
            caption="فهرست بازاریاب‌ها"
            columns={RecipientColumns()}
            mobileSummary={(row) =>
              phoneSummary({
                meta: (
                  <>
                    <CodeText>{row.recipientCode}</CodeText>
                    {` · ${row.payoutAccountVerified ? 'حساب تأییدشده' : 'حساب تأیید نشده'}`}
                  </>
                ),
                status: row.status,
                title: (
                  <Link to={`/marketing/recipients/${row.id}`}>
                    {row.user.displayNameFa ?? row.recipientCode}
                  </Link>
                ),
              })
            }
            rowKey={(row) => row.id}
            rows={rows}
          />
          <ListFooter
            pager={
              <ListLoadMore
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                onLoadMore={() => void list.fetchNextPage()}
              />
            }
            shown={rows.length}
            total={firstPageTotal(list.data)}
          />
        </Section>
      </QueryState>
      {creating ? <CreateRecipientDialog onOpenChange={setCreating} /> : null}
    </>
  );
}

const statusSchema = z.object({
  reason: reasonSchema(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});
type StatusValues = z.input<typeof statusSchema>;

export function MarketingRecipientPage() {
  const { recipientId = '' } = useParams();
  const { self } = useOutletContext<MarketingOutletContext>();
  const recipient = useMarketingRecipient(recipientId);
  const data = recipient.data;
  usePageTitle(data?.user.displayNameFa ?? 'بازاریاب');
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const manage = canMarket(self, 'updateMarketingRecipient');
  const canAccount = canUse(self, 'requestPayoutAccount');
  return (
    <>
      <PageHeader
        actions={
          data !== undefined && manage ? (
            <Button onClick={() => setEditing(true)} type="button" variant="secondary">
              تغییر وضعیت
            </Button>
          ) : undefined
        }
        eyebrow={<Link to="/marketing/recipients">بازاریاب‌ها</Link>}
        title={data?.user.displayNameFa ?? 'بازاریاب'}
      />
      <QueryState
        error={recipient.error}
        onRetry={() => void recipient.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری بازاریاب…" lines={6} />}
        status={recipient.status}
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <Section id="recipient-identity" title="مشخصات">
              <p>
                کد <CodeText>{data.recipientCode}</CodeText>
              </p>
              <p>
                وضعیت <MarketingStatus value={data.status} />
              </p>
              <p>حساب شخص: {statusLabel('identityUserStatus', data.user.status)}</p>
              <p>موبایل: {data.user.mobileMasked}</p>
              <p>اتصال فعال: {formatCount(data.activeAssignmentCount)}</p>
              {data.createdBy === undefined ? null : (
                <p>{`ثبت توسط ${data.createdBy.displayNameFa} در ${formatTimestamp(data.createdAt)}`}</p>
              )}
            </Section>
            {data.payoutAccount === undefined ? null : (
              <Section id="recipient-payout" title="حساب پرداخت">
                {data.payoutAccount === null ? (
                  <p>حساب تأییدشده‌ای ندارد.</p>
                ) : (
                  <p>
                    <CodeText>{data.payoutAccount.shebaMasked}</CodeText>
                    {` — ${data.payoutAccount.holderName} — ${statusLabel('payoutAccountVerificationStatus', data.payoutAccount.verificationStatus)}`}
                  </p>
                )}
                {canAccount ? <RecipientPayoutDialog recipient={data} /> : null}
              </Section>
            )}
          </div>
        )}
      </QueryState>
      {editing && data !== undefined ? (
        <CommandDialog<
          StatusValues,
          { expectedVersion: number; reason: string; status: 'ACTIVE' | 'INACTIVE' },
          MarketingRecipient
        >
          defaultValues={{ reason: '', status: data.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }}
          fields={['reason', 'status']}
          guidance={marketingGuidance}
          name="marketing.recipient.status"
          onOpenChange={setEditing}
          onSuccess={async (result) => {
            await recipient.refetch();
            toast.success({ confirmedBy: result, title: 'وضعیت بازاریاب ذخیره شد' });
          }}
          schema={statusSchema as never}
          send={(client, body, intent) =>
            client.patch(`/admin/marketing-recipients/${encodeURIComponent(data.id)}`, {
              body,
              intent,
            })
          }
          submitLabel="ذخیره وضعیت"
          title="تغییر وضعیت بازاریاب"
          toBody={(values) => ({
            expectedVersion: data.version,
            reason: values.reason.trim(),
            status: values.status,
          })}
        >
          {(form) => (
            <>
              <Controller
                control={form.control}
                name="status"
                render={({ field }) => (
                  <div className="flex gap-2" role="group" aria-label="وضعیت">
                    <Button
                      onClick={() => field.onChange('ACTIVE')}
                      type="button"
                      variant={field.value === 'ACTIVE' ? 'primary' : 'secondary'}
                    >
                      فعال
                    </Button>
                    <Button
                      onClick={() => field.onChange('INACTIVE')}
                      type="button"
                      variant={field.value === 'INACTIVE' ? 'primary' : 'secondary'}
                    >
                      غیرفعال
                    </Button>
                  </div>
                )}
              />
              <TextField
                error={form.formState.errors.reason?.message}
                label="دلیل"
                {...form.register('reason')}
              />
            </>
          )}
        </CommandDialog>
      ) : null}
    </>
  );
}

function RecipientPayoutDialog({ recipient }: { readonly recipient: MarketingRecipient }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} type="button">
        ثبت حساب
      </Button>
    );
  }
  return (
    <CommandDialog<
      { holderName: string; reason: string; sheba: string },
      {
        holderName: string;
        ownerId: string;
        ownerType: 'MARKETING_RECIPIENT';
        reason: string;
        sheba: string;
      },
      { id: string }
    >
      defaultValues={{ holderName: recipient.user.displayNameFa ?? '', reason: '', sheba: '' }}
      fields={['sheba', 'holderName', 'reason']}
      guidance={marketingGuidance}
      name="marketing.payout.request"
      onOpenChange={setOpen}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({
          queryKey: setupKeys.marketingRecipient(recipient.id),
        });
        toast.success({ confirmedBy: result, title: 'حساب برای تأیید ثبت شد' });
      }}
      schema={
        z.object({
          holderName: z.string().trim().min(1, 'نام صاحب حساب را بنویسید.').max(200),
          reason: reasonSchema(),
          sheba: z.string().trim().min(1, 'شبا را بنویسید.'),
        }) as never
      }
      send={(client, body, intent) => client.post('/admin/payout-accounts', { body, intent })}
      submitLabel="ثبت حساب"
      title="حساب پرداخت بازاریاب"
      toBody={(values) => ({
        holderName: values.holderName.trim(),
        ownerId: recipient.id,
        ownerType: 'MARKETING_RECIPIENT',
        reason: values.reason.trim(),
        sheba: values.sheba.replace(/\s/g, '').toUpperCase(),
      })}
    >
      {(form) => (
        <>
          <Controller
            control={form.control}
            name="sheba"
            render={({ field, fieldState }) => (
              <TextField
                error={fieldState.error?.message}
                label="شماره شبا"
                ltr
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                ref={field.ref}
                value={field.value}
              />
            )}
          />
          <Controller
            control={form.control}
            name="holderName"
            render={({ field, fieldState }) => (
              <TextField
                error={fieldState.error?.message}
                label="نام صاحب حساب"
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                ref={field.ref}
                value={field.value}
              />
            )}
          />
          <Controller
            control={form.control}
            name="reason"
            render={({ field, fieldState }) => (
              <TextField
                error={fieldState.error?.message}
                label="دلیل"
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                ref={field.ref}
                value={field.value}
              />
            )}
          />
          <p className="text-sm text-muted-foreground">
            اگر حساب قبلی تأیید شده باشد، پرداخت کمیسیون یک دوره متوقف می‌شود. ثبت حساب ممکن است
            برای تأیید مدیر دیگر برود.
          </p>
        </>
      )}
    </CommandDialog>
  );
}

function StructureColumns(): readonly DataTableColumn<MarketingStructureSummary>[] {
  return [
    {
      header: 'ساختار',
      id: 'name',
      render: (row) => <Link to={`/marketing/structures/${row.id}`}>{row.nameFa}</Link>,
    },
    { header: 'کد', id: 'code', render: (row) => <CodeText>{row.code}</CodeText> },
    {
      header: 'نسخه منتشرشده',
      id: 'published',
      render: (row) =>
        row.publishedVersion === null
          ? 'ندارد'
          : `${formatCount(row.publishedVersion.versionNumber)} · ${formatCount(row.publishedVersion.layerCount)} لایه`,
    },
  ];
}

const layerSchema = z.object({
  baseType: z.enum(['PURCHASE_AMOUNT', 'SAAY_FEE']),
  percentage: z.string().min(1, 'درصد را بنویسید.'),
  roleLabelFa: z.string().trim().min(1, 'نام لایه را بنویسید.').max(100),
});

const structureSchema = z.object({
  code: CODE,
  layers: z.array(layerSchema).min(1, 'دست‌کم یک لایه لازم است.').max(7),
  nameFa: z.string().trim().min(1, 'نام را بنویسید.').max(200),
});
type StructureValues = z.input<typeof structureSchema>;

function toLayers(values: StructureValues): MarketingLayerInput[] {
  return values.layers.map((layer, index) => ({
    baseType: layer.baseType,
    layerNumber: index + 1,
    percentage: layer.percentage,
    roleLabelFa: layer.roleLabelFa.trim(),
  }));
}

function LayerEditor({
  form,
}: {
  readonly form: UseFormReturn<StructureValues, unknown, StructureValues>;
}) {
  const layers = useFieldArray({ control: form.control, name: 'layers' });
  return (
    <div className="flex flex-col gap-3">
      {layers.fields.map((field, index) => (
        <Surface key={field.id}>
          <p className="font-bold">{`لایه ${formatCount(index + 1)}`}</p>
          <TextField
            error={form.formState.errors.layers?.[index]?.roleLabelFa?.message}
            label="نام لایه"
            {...form.register(`layers.${index}.roleLabelFa`)}
          />
          <Controller
            control={form.control}
            name={`layers.${index}.baseType`}
            render={({ field: base }) => (
              <div className="flex gap-2" role="group" aria-label="پایه محاسبه">
                {(['PURCHASE_AMOUNT', 'SAAY_FEE'] as const).map((value) => (
                  <Button
                    key={value}
                    onClick={() => base.onChange(value)}
                    type="button"
                    variant={base.value === value ? 'primary' : 'secondary'}
                  >
                    {marketingLabel(value)}
                  </Button>
                ))}
              </div>
            )}
          />
          <Controller
            control={form.control}
            name={`layers.${index}.percentage`}
            render={({ field: rate, fieldState }) => (
              <PercentField
                error={fieldState.error?.message}
                label="درصد"
                onChangeRate={(next) => rate.onChange(next ?? '')}
                valueRate={rate.value === '' ? null : rate.value}
              />
            )}
          />
          {layers.fields.length > 1 ? (
            <Button onClick={() => layers.remove(index)} type="button" variant="ghost">
              حذف لایه
            </Button>
          ) : null}
        </Surface>
      ))}
      {layers.fields.length < 7 ? (
        <Button
          onClick={() =>
            layers.append({ baseType: 'PURCHASE_AMOUNT', percentage: '', roleLabelFa: '' })
          }
          type="button"
          variant="secondary"
        >
          لایه بعدی
        </Button>
      ) : null}
    </div>
  );
}

const emptyLayer = { baseType: 'PURCHASE_AMOUNT' as const, percentage: '', roleLabelFa: '' };

function CreateStructureDialog({
  onOpenChange,
}: {
  readonly onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  return (
    <CommandDialog<StructureValues, MarketingStructureCreate, MarketingStructure>
      defaultValues={{ code: '', layers: [emptyLayer], nameFa: '' }}
      fields={['code', 'nameFa', 'layers']}
      guidance={marketingGuidance}
      name="marketing.structure.create"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.marketingStructures });
        toast.success({ confirmedBy: result, title: 'ساختار به‌صورت پیش‌نویس ثبت شد' });
        void navigate(`/marketing/structures/${result.data.id}`);
      }}
      schema={structureSchema as never}
      send={(client, body, intent) => client.post('/admin/marketing-structures', { body, intent })}
      size="wide"
      submitLabel="ثبت ساختار"
      title="ساختار تازه"
      toBody={(values) => {
        const parsed = structureSchema.parse(values);
        return { code: parsed.code, layers: toLayers(parsed), nameFa: parsed.nameFa.trim() };
      }}
    >
      {(form) => (
        <>
          <TextField
            error={form.formState.errors.code?.message}
            label="کد ساختار"
            ltr
            {...form.register('code', {
              onChange: (event) => {
                event.target.value = normalizeAdminCode(event.target.value);
              },
            })}
          />
          <TextField
            error={form.formState.errors.nameFa?.message}
            label="نام"
            {...form.register('nameFa')}
          />
          <LayerEditor form={form} />
        </>
      )}
    </CommandDialog>
  );
}

export function MarketingStructuresPage() {
  usePageTitle('ساختارهای کمیسیون');
  const { self } = useOutletContext<MarketingOutletContext>();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const list = useMarketingStructures(q === '' ? {} : { q });
  const rows = pageRows(list.data);
  const [creating, setCreating] = useState(false);
  const search = useSearchInput({
    applied: q,
    minLength: 1,
    onApply: (value) => setParams(value === '' ? {} : { q: value }),
  });
  return (
    <>
      <PageHeader
        actions={
          canMarket(self, 'createMarketingStructure') ? (
            <Button onClick={() => setCreating(true)} type="button">
              ساختار تازه
            </Button>
          ) : undefined
        }
        title="ساختارهای کمیسیون"
      />
      <QueryState
        empty={<EmptyState title="ساختاری ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری ساختارها…" lines={6} />}
        status={listStatus(list)}
      >
        <Section id="marketing-structures" title="فهرست ساختارها">
          <FilterPillBar
            activeFilterCount={q === '' ? 0 : 1}
            label="فیلتر ساختارها"
            onClearAll={() => setParams({})}
          >
            <form
              className="shrink-0"
              onSubmit={(event) => {
                event.preventDefault();
                search.submit();
              }}
            >
              <ListSearchInput
                label="جست‌وجوی ساختار"
                onChange={(event) => search.setText(event.target.value)}
                placeholder="نام یا کد ساختار"
                value={search.text}
              />
            </form>
          </FilterPillBar>
          <AdminListTable
            caption="فهرست ساختارها"
            columns={StructureColumns()}
            mobileSummary={(row) =>
              phoneSummary({
                meta: (
                  <>
                    <CodeText>{row.code}</CodeText>
                    {row.publishedVersion === null
                      ? ' · بدون نسخه'
                      : ` · نسخه ${formatCount(row.publishedVersion.versionNumber)}`}
                  </>
                ),
                title: <Link to={`/marketing/structures/${row.id}`}>{row.nameFa}</Link>,
              })
            }
            rowKey={(row) => row.id}
            rows={rows}
          />
        </Section>
      </QueryState>
      {creating ? <CreateStructureDialog onOpenChange={setCreating} /> : null}
    </>
  );
}

function VersionCard({
  manage,
  onChanged,
  version,
}: {
  readonly manage: boolean;
  readonly onChanged: () => Promise<void>;
  readonly version: MarketingStructureVersion;
}) {
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const draft = version.status === 'DRAFT';
  return (
    <Section id={`version-${version.id}`} title={`نسخه ${formatCount(version.versionNumber)}`}>
      <MarketingStatus value={version.status} />
      <p>
        {`سهم مبلغ خرید ${formatRatePercent(version.purchaseAmountRate, { invalid: 'raw' })} · سهم کارمزد ${formatRatePercent(version.saayFeeShareRate, { invalid: 'raw' })}`}
      </p>
      <ul className="flex flex-col gap-2">
        {version.layers.map((layer) => (
          <li key={layer.id}>
            {`${formatCount(layer.layerNumber)}. ${layer.roleLabelFa} · ${marketingLabel(layer.baseType)} · ${formatRatePercent(layer.percentage, { invalid: 'raw' })}`}
          </li>
        ))}
      </ul>
      {manage && draft ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setEditing(true)} type="button" variant="secondary">
            ویرایش پیش‌نویس
          </Button>
          <PublishButton
            onPublished={async (result) => {
              await onChanged();
              toast.success({ confirmedBy: result, title: 'نسخه منتشر شد' });
            }}
            version={version}
          />
        </div>
      ) : null}
      {editing ? (
        <CommandDialog<StructureValues, MarketingStructureVersionUpdate, MarketingStructureVersion>
          defaultValues={{
            code: 'KEEP-CODE',
            layers: version.layers.map((layer) => ({
              baseType: layer.baseType,
              percentage: layer.percentage,
              roleLabelFa: layer.roleLabelFa,
            })),
            nameFa: 'پیش‌نویس',
          }}
          fields={['layers']}
          guidance={marketingGuidance}
          name="marketing.structure.version.update"
          onOpenChange={setEditing}
          onSuccess={async (result) => {
            await onChanged();
            toast.success({ confirmedBy: result, title: 'پیش‌نویس ذخیره شد' });
          }}
          schema={structureSchema as never}
          send={(client, body, intent) =>
            client.patch(
              `/admin/marketing-structures/${encodeURIComponent(version.structureId)}/versions/${encodeURIComponent(version.id)}`,
              { body, intent },
            )
          }
          submitLabel="ذخیره پیش‌نویس"
          title="ویرایش لایه‌ها"
          toBody={(values) => ({
            expectedVersion: version.version,
            layers: toLayers(structureSchema.parse(values)),
          })}
        >
          {(form) => <LayerEditor form={form} />}
        </CommandDialog>
      ) : null}
    </Section>
  );
}

function PublishButton({
  onPublished,
  version,
}: {
  readonly onPublished: (result: ApiResult<MarketingStructureVersion>) => Promise<void>;
  readonly version: MarketingStructureVersion;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} type="button">
        انتشار نسخه
      </Button>
    );
  }
  return (
    <CommandDialog<{ confirm: string }, { expectedVersion: number }, MarketingStructureVersion>
      defaultValues={{ confirm: '' }}
      fields={[]}
      guidance={marketingGuidance}
      name="marketing.structure.publish"
      onOpenChange={setOpen}
      onSuccess={onPublished}
      schema={z.object({ confirm: z.string() }) as never}
      send={(client, body, intent) =>
        client.post(
          `/admin/marketing-structures/${encodeURIComponent(version.structureId)}/versions/${encodeURIComponent(version.id)}/publication`,
          { body, intent },
        )
      }
      submitLabel="انتشار"
      title="انتشار نسخه"
      toBody={() => ({ expectedVersion: version.version })}
    >
      {() => <p>بعد از انتشار، این نسخه دیگر تغییر نمی‌کند و نسخهٔ قبلی بازنشسته می‌شود.</p>}
    </CommandDialog>
  );
}

export function MarketingStructurePage() {
  const { structureId = '' } = useParams();
  const { self } = useOutletContext<MarketingOutletContext>();
  const structure = useMarketingStructure(structureId);
  const data = structure.data;
  usePageTitle(data?.nameFa ?? 'ساختار');
  const [creatingVersion, setCreatingVersion] = useState(false);
  const toast = useToast();
  const manage = canMarket(self, 'createMarketingStructureVersion');
  const hasDraft = data?.versions.some((version) => version.status === 'DRAFT') === true;
  return (
    <>
      <PageHeader
        actions={
          manage && data !== undefined && !hasDraft ? (
            <Button onClick={() => setCreatingVersion(true)} type="button" variant="secondary">
              نسخه تازه
            </Button>
          ) : undefined
        }
        eyebrow={<Link to="/marketing/structures">ساختارها</Link>}
        title={data?.nameFa ?? 'ساختار'}
      />
      <QueryState
        error={structure.error}
        onRetry={() => void structure.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری ساختار…" lines={6} />}
        status={structure.status}
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <p>
              کد <CodeText>{data.code}</CodeText>
            </p>
            {data.versions.map((version) => (
              <VersionCard
                key={version.id}
                manage={canMarket(self, 'updateMarketingStructureVersion')}
                onChanged={async () => {
                  await structure.refetch();
                }}
                version={version}
              />
            ))}
          </div>
        )}
      </QueryState>
      {creatingVersion && data !== undefined ? (
        <CommandDialog<
          StructureValues,
          { layers: MarketingLayerInput[] },
          MarketingStructureVersion
        >
          defaultValues={{ code: data.code, layers: [emptyLayer], nameFa: data.nameFa }}
          fields={['layers']}
          guidance={marketingGuidance}
          name="marketing.structure.version.create"
          onOpenChange={setCreatingVersion}
          onSuccess={async (result) => {
            await structure.refetch();
            toast.success({ confirmedBy: result, title: 'پیش‌نویس نسخه ساخته شد' });
          }}
          schema={structureSchema as never}
          send={(client, body, intent) =>
            client.post(`/admin/marketing-structures/${encodeURIComponent(data.id)}/versions`, {
              body,
              intent,
            })
          }
          submitLabel="ساخت پیش‌نویس"
          title="نسخه تازه"
          toBody={(values) => ({ layers: toLayers(structureSchema.parse(values)) })}
        >
          {(form) => <LayerEditor form={form} />}
        </CommandDialog>
      ) : null}
    </>
  );
}
